import React from 'react';
import { useTranslation } from 'react-i18next';

import { Alert } from '@/components/atoms/Alert';
import { apiErrorContent } from '@/components/atoms/ApiErrorMessage';
import { Button } from '@/components/atoms/Buttons';
import FormField from '@/components/atoms/FormField';
import TextField from '@/components/atoms/TextField';

import { PublicVideoLayout } from '../PublicVideoLayout';

import styles from '../public.module.scss';

export interface UserRegisterFormSubmit {
    firstName: string;
    lastName: string;
    password: string;
    token: string;
}

interface UserRegisterFormState {
    firstName: string;
    lastName: string;
    password: string;
    confirmPassword: string;
}

const defaultState = {
    firstName: '',
    lastName: '',
    password: '',
    confirmPassword: '',
};

interface UserInviteFormProps {
    token: string;
    onSubmit: (state: UserRegisterFormSubmit) => Promise<unknown>;
}

export function UserRegister({ token, onSubmit }: UserInviteFormProps) {
    const [state, setState] = React.useState<UserRegisterFormState>(defaultState);
    const { t } = useTranslation();
    const [disabled, setDisabled] = React.useState(false);
    const [error, setError] = React.useState<React.ReactNode>(null);
    const [success, setSuccess] = React.useState(false);
    const invalidPassword = state.password !== state.confirmPassword;
    const invalid =
        invalidPassword ||
        state.password === '' ||
        state.firstName.trim() === '' ||
        state.lastName.trim() === '';

    const updateState = (partialState: Partial<UserRegisterFormState>) =>
        setState((s) => ({ ...s, ...partialState }));

    const submit = async (e: React.FormEvent<HTMLFormElement>): Promise<void> => {
        try {
            e.preventDefault();
            setError(null);
            setDisabled(true);
            await onSubmit({ ...state, token });
            setSuccess(true);
        } catch (e) {
            setError(
                apiErrorContent(e, undefined, (message) =>
                    t('publicPages.userRegister.error', { message })
                )
            );
            setDisabled(false);
            setSuccess(false);
        }
    };

    return (
        <PublicVideoLayout>
            <h2>{t('publicPages.userRegister.register')}</h2>
            <form onSubmit={submit}>
                <FormField
                    label={t('publicPages.userRegister.firstName')}
                    labelClassName={styles.inverseLabel}>
                    <TextField
                        className={styles.formControl}
                        type="text"
                        name="firstName"
                        data-testid="firstName"
                        placeholder={t('publicPages.userRegister.firstName')}
                        value={state.firstName}
                        onChange={(e) =>
                            updateState({
                                firstName: e.target.value,
                            })
                        }
                    />
                </FormField>
                <FormField
                    label={t('publicPages.userRegister.lastName')}
                    labelClassName={styles.inverseLabel}>
                    <TextField
                        className={styles.formControl}
                        type="text"
                        name="lastName"
                        data-testid="lastName"
                        placeholder={t('publicPages.userRegister.lastName')}
                        value={state.lastName}
                        onChange={(e) =>
                            updateState({
                                lastName: e.target.value,
                            })
                        }
                    />
                </FormField>
                <FormField
                    label={t('publicPages.common.password')}
                    labelClassName={styles.inverseLabel}>
                    <TextField
                        className={styles.formControl}
                        type="password"
                        name="password"
                        placeholder={t('publicPages.common.password')}
                        data-testid="password"
                        value={state.password}
                        onChange={(e) =>
                            updateState({
                                password: e.target.value,
                            })
                        }
                    />
                </FormField>
                <FormField
                    label={t('publicPages.userRegister.repeatPassword')}
                    labelClassName={styles.inverseLabel}>
                    <TextField
                        className={styles.formControl}
                        type="password"
                        name="confirmPassword"
                        placeholder={t('publicPages.common.password')}
                        value={state.confirmPassword}
                        invalid={invalidPassword && state.password.length > 0}
                        data-testid="confirmPassword"
                        onChange={(e) =>
                            updateState({
                                confirmPassword: e.target.value,
                            })
                        }
                    />
                </FormField>

                {error && <Alert tone="danger">{error}</Alert>}
                {success && <Alert tone="success">{t('publicPages.userRegister.success')}</Alert>}
                <div className={styles.actions}>
                    <Button
                        variant="primary"
                        type="submit"
                        data-testid="submitButton"
                        fullWidth
                        disabled={disabled || invalid}>
                        {t('publicPages.userRegister.createUser')}
                    </Button>
                </div>
            </form>
        </PublicVideoLayout>
    );
}
